import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionAccess';

interface UserMenuProps {
  onNavigate: (path: string) => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const { status: subStatus } = useSubscriptionStatus();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

  // Calculate dropdown position using getBoundingClientRect
  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [isOpen, updatePosition]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isOpen) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!user) {
    return null;
  }

  const initials = user.full_name
    ? user.full_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : user.email[0].toUpperCase();

  const menuItems = [
    { icon: '🏠', label: 'Моя страница', action: () => { onNavigate('/dashboard'); setIsOpen(false); } },
    { icon: '👤', label: 'Профиль', action: () => { onNavigate('/profile'); setIsOpen(false); } },
    { icon: '💎', label: 'Подписка', action: () => { onNavigate('/subscription'); setIsOpen(false); } },
    { icon: '👥', label: 'Контакты', action: () => { onNavigate('/profile?tab=contacts'); setIsOpen(false); } },
    { icon: '🔔', label: 'Уведомления', action: () => { onNavigate('/profile?tab=notifications'); setIsOpen(false); } },
    { divider: true },
    { icon: '🚪', label: 'Выйти', action: async () => { setIsOpen(false); await logout(); onNavigate('/'); } },
  ];

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Меню пользователя"
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: 'rgba(212, 175, 55, 0.1)',
          border: '1px solid rgba(212, 175, 55, 0.3)',
          borderRadius: '25px', padding: '6px 16px 6px 6px',
          cursor: 'pointer', transition: 'all 0.3s',
          color: '#fff',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(212, 175, 55, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{
          width: '36px', height: '36px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#0a0a1a', fontWeight: 'bold', fontSize: '14px',
        }}>
          {initials}
        </div>
        <span style={{ fontSize: '14px', fontWeight: '500', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.full_name || user.email}
        </span>
        <span style={{ fontSize: '10px', color: '#d4af37' }}>▼</span>
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Меню пользователя"
          style={{
            position: 'fixed',
            top: `${dropdownPos.top}px`,
            right: `${dropdownPos.right}px`,
            background: 'rgba(13, 20, 40, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            borderRadius: '16px', padding: '8px 0',
            minWidth: '220px',
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(212, 175, 55, 0.1)',
            zIndex: 100001,
            maxWidth: 'calc(100vw - 40px)',
          }}
          className="user-menu-dropdown"
        >
          {/* User info header */}
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
            marginBottom: '4px',
          }}>
            <div style={{ color: '#d4af37', fontSize: '14px', fontWeight: '600' }}>
              {user.full_name || 'Пользователь'}
            </div>
            <div style={{ color: '#888', fontSize: '12px', marginTop: '2px' }}>
              {user.email}
            </div>
            <div style={{
              display: 'inline-block', marginTop: '6px',
              background: subStatus?.isActive ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255,255,255,0.1)',
              color: subStatus?.isActive ? '#d4af37' : '#888',
              padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
            }}>
              {subStatus?.isActive ? `${subStatus.tier} · ${subStatus.daysLeft} дн.` : 'Free'}
            </div>
          </div>

          {menuItems.map((item, i) => {
            if ('divider' in item && item.divider) {
              return <div key={i} style={{ height: '1px', background: 'rgba(212, 175, 55, 0.15)', margin: '4px 0' }} />;
            }
            return (
              <button
                key={i}
                onClick={item.action}
                role="menuitem"
                aria-label={item.label}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  width: '100%', padding: '10px 16px',
                  background: 'transparent', border: 'none',
                  color: '#ccc', fontSize: '14px', cursor: 'pointer',
                  textAlign: 'left', transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)';
                  e.currentTarget.style.color = '#d4af37';
                  e.currentTarget.style.paddingLeft = '20px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#ccc';
                  e.currentTarget.style.paddingLeft = '16px';
                }}
              >
                <span aria-hidden="true">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserMenu;
